import { inject, Injectable, signal } from '@angular/core';
import { CapacitorShareError, CONFIG_TOKEN } from '@pxl/models/common';
import {
    DialogService,
    LAZY_DIALOG_REGISTRY,
    LoadingService,
    LoggingService,
    LoginService,
    PdfViewerService,
    ShareService,
} from '@pxl/models/contracts';
import { DocumentModel, DocumentService as DocumentBackendService } from '@pxl/shared/generated';
import { Observable, of } from 'rxjs';
import { map, take } from 'rxjs/operators';
@Injectable({
    providedIn: 'root',
})
export class DefaultPdfViewerService implements PdfViewerService {
    protected $blobUrl = signal<string | null>(null);
    protected $pdfFileName = signal<string>('document');
    protected $blob = signal<Blob | null>(null);
    private readonly config = inject(CONFIG_TOKEN);
    private readonly documentBackendService = inject(DocumentBackendService);
    private readonly loginService = inject(LoginService);
    private readonly loadingService = inject(LoadingService);
    private readonly dialogService = inject(DialogService);
    private readonly loggingService = inject(LoggingService);
    private readonly shareService = inject(ShareService);
    private readonly lazyDialogs = inject(LAZY_DIALOG_REGISTRY);

    public showPdf(documentModel: DocumentModel, dialogTitleResourceKey?: string, pdfFileName?: string): void {
        if (pdfFileName) {
            this.$pdfFileName.set(pdfFileName);
        }
        this.openPdf(this.downloadAndCreateBlobUrl(documentModel), dialogTitleResourceKey);
    }

    public showPdfFromBase64(base64: string, dialogTitleResourceKey?: string, pdfFileName?: string): void {
        this.openPdf(this.setDocumentAndCreateBlobUrl(base64, pdfFileName), dialogTitleResourceKey);
    }

    public showPdfFromUrl(url: string, dialogTitleResourceKey?: string, pdfFileName?: string): void {
        if (pdfFileName) {
            this.$pdfFileName.set(pdfFileName);
        }
        // The viewer fetches the signed URL directly — no download, no blob.
        this.openPdf(of(url), dialogTitleResourceKey);
    }

    private openPdf(pdfUrl$: Observable<string | undefined>, dialogTitleResourceKey?: string): void {
        this.loadingService
            .showWaitObservable(pdfUrl$)
            .pipe(take(1))
            .subscribe({
                next: (pdfUrl) => {
                    if (pdfUrl) {
                        this.lazyDialogs.PdfViewer().then((PdfViewerDialogComponent) => {
                            this.dialogService.open(PdfViewerDialogComponent, { pdfUrl, dialogTitleResourceKey });
                        });
                    }
                },
                error: (err) => {
                    this.loggingService.error(err);
                },
            });
    }

    public downloadPdf(blob?: Blob): void {
        // For URL sources no blob URL was pre-created — derive one from the blob the viewer provides.
        const blobUrl = blob ? URL.createObjectURL(blob) : this.$blobUrl();
        if (blobUrl) {
            const htmlAnchorElement = document.createElement('a');
            htmlAnchorElement.href = blobUrl;
            htmlAnchorElement.download = this.$pdfFileName();
            htmlAnchorElement.click();
            if (blob) {
                URL.revokeObjectURL(blobUrl);
            }
        }
    }

    public sharePdf(blob?: Blob): void {
        const pdfBlob = blob ?? this.$blob();
        const pdfFileName = this.$pdfFileName();
        if (pdfBlob) {
            this.shareService.share(pdfFileName, pdfBlob).catch((error: CapacitorShareError) => {
                if (error.type !== 'userCancelledShare') {
                    this.loggingService.error(error);
                }
            });
        }
    }

    private downloadAndCreateBlobUrl(documentModel: DocumentModel): Observable<string | undefined> {
        return this.downloadDocument(documentModel.id).pipe(map((d) => this.createBlobUrl(d)));
    }
    public setDocumentAndCreateBlobUrl(document: string, pdfFileName?: string): Observable<string | undefined> {
        if (pdfFileName) this.$pdfFileName.set(pdfFileName);
        return of(document).pipe(
            map(this.base64ToArrayBuffer),
            map((d) => this.createBlobUrl(d)),
        );
    }

    private downloadDocument(documentId: string) {
        const token = this.loginService.getToken() || '';
        return this.documentBackendService.downloadDocument(documentId, this.config.API_VERSION, token).pipe(map(this.base64ToArrayBuffer));
    }

    private createBlobUrl(document: ArrayBuffer | undefined): string | undefined {
        if (!document) return undefined;
        const blob = new Blob([document], { type: 'application/pdf' });
        const blobUrl = URL.createObjectURL(blob);
        this.$blob.set(blob);
        this.$blobUrl.set(blobUrl);
        return blobUrl;
    }

    private base64ToArrayBuffer(base64: string): ArrayBuffer {
        const binary_string = atob(base64);
        const len = binary_string.length;
        const bytes = new Uint8Array(len);
        for (let i = 0; i < len; i++) {
            bytes[i] = binary_string.charCodeAt(i);
        }
        return bytes.buffer;
    }
}
